/**
 * OpenCodeTuiAdapter — drives the `opencode` TUI (https://opencode.ai) with
 * STRUCTURED EVENTS: the Conveyor events plugin (harness/opencode/plugin.ts)
 * rides the spawn config and appends the client engine's bus events to an
 * NDJSON sink the session tails — the same trusted-source pattern as Claude's
 * transcript JSONL, so a card gets chat mirroring, activity summaries, real
 * turn completion (the session.idle signal) and MCP tools while the human
 * watches and types in the live TUI.
 *
 * Config (MCP entries, instructions, permission, plugin) rides
 * OPENCODE_CONFIG_CONTENT on the child env. That works because the interactive
 * TUI runs the CLIENT engine — the same one `opencode run` uses, where all of
 * it was verified live (#3893 + the 2026-08-08 plugin probes). The permission
 * allow-all block matters doubly here: the TUI has no `--auto`, so it is the
 * only thing between an autonomous card and a permission dialog nobody
 * answers.
 *
 * Auth is env-based for api_key keys (CONVEYOR_AGENT_KEY → provider-native env
 * var) and store-based for chatgpt_oauth keys (CONVEYOR_OPENCODE_OAUTH seeded
 * into opencode's auth.json by prepareEnvironment — see opencode-auth.ts).
 */
import { buildPromptBytes, inheritedEnv } from "../pty-support.js";
import { cleanTerminalOutput } from "../spawn-args.js";
import {
  buildOpenCodeCredentialEnv,
  prepareOpenCodeCredentials,
  resolveOpenCodeModel,
} from "../../opencode/credentials.js";
import { buildOpenCodeConfigContent } from "../../opencode/spawn.js";
import { OPENCODE_EVENTS_FILE_ENV } from "../../opencode/plugin.js";
import {
  findOnPath,
  TuiUnavailableError,
  type TuiAdapter,
  type TuiCapabilities,
  type TuiFingerprintInput,
  type TuiSpawnInput,
  type TuiSpawnSpec,
} from "./types.js";

export class OpenCodeTuiAdapter implements TuiAdapter {
  readonly id = "opencode" as const;
  readonly capabilities: TuiCapabilities = {
    // `--session <ses_…>` continues the id the plugin events latched.
    resume: true,
    structuredEvents: true,
    // The input box tolerates paste-without-submit, but manual prefill has no
    // card users yet — keep the initial-query behavior unchanged for now.
    prefill: false,
    // Plugin events arriving while parked ARE the passive signal.
    passiveTurns: true,
    // opencode silently discards early stdin while the TUI paints; the pasted
    // text itself is lost, so the readiness probe must gate the first write
    // even though structured events exist now.
    rawPromptGate: true,
  };

  constructor(private readonly env: NodeJS.ProcessEnv = process.env) {}

  resolveBinary(env: NodeJS.ProcessEnv = this.env): string {
    const override = env.CONVEYOR_OPENCODE_BIN;
    const found = override ? findOnPath(override, env) : findOnPath("opencode", env);
    if (!found) {
      throw new TuiUnavailableError(
        "opencode",
        "The opencode CLI is not available in this environment. It must be baked " +
          "into the pod image (see Dockerfile.base) — re-run Build Image for this " +
          "project, or set CONVEYOR_OPENCODE_BIN to its absolute path.",
      );
    }
    return found;
  }

  buildSpawn(input: TuiSpawnInput): TuiSpawnSpec {
    const env = { ...inheritedEnv() };
    // Credential resolution is shared with the headless card harness so the two
    // paths cannot drift on how a deployed pod authenticates.
    const credentials = buildOpenCodeCredentialEnv(this.env);
    for (const key of credentials.unset) delete env[key];
    Object.assign(env, credentials.set);

    // Read-only modes arrive as Claude's permissionMode "plan"; the config
    // denies bind the model AND human-typed turns (same engine, same config).
    const configContent = buildOpenCodeConfigContent({
      entries: input.mcpEntries ?? {},
      systemPromptPath: input.instructionsPath ?? null,
      readOnly: input.options.permissionMode === "plan",
      pluginPath: input.pluginPath ?? null,
    });
    if (configContent) env.OPENCODE_CONFIG_CONTENT = configContent;
    if (input.eventsSinkPath) env[OPENCODE_EVENTS_FILE_ENV] = input.eventsSinkPath;

    const model = resolveOpenCodeModel(this.env, input.options.model);
    const args: string[] = [];
    if (model) args.push("--model", model);
    // Continue the opencode-assigned session (latched from the plugin events)
    // after a respawn/wake, instead of starting a fresh conversation.
    if (input.resume) args.push("--session", input.resume);
    return { file: this.resolveBinary(), args, env };
  }

  async prepareEnvironment(): Promise<void> {
    // ChatGPT-subscription tokens (when present) are seeded into opencode's
    // auth store; session-state persistence stays the entrypoint's job
    // (GCS-FUSE symlinks for ~/.local/share/opencode).
    await prepareOpenCodeCredentials(this.env);
  }

  spawnFingerprint(input: TuiFingerprintInput): string {
    // appendSystemPrompt is spawn-time state here (it becomes the instructions
    // file baked into the child's config env), so drift must invalidate a
    // parked process — same rationale as Claude's fingerprint.
    return JSON.stringify([
      "opencode",
      input.model,
      input.cwd,
      input.permissionMode,
      input.appendSystemPrompt ?? "",
    ]);
  }

  encodePromptBytes(text: string): string {
    // Bracketed paste — standard for modern TUIs; revisit if live verification
    // shows opencode's input mishandles it.
    return buildPromptBytes(text);
  }

  buildExitErrors(exitCode: number, rawOutput: string): string[] {
    const errors = [`opencode exited (code ${exitCode}) without a result`];
    const tail = cleanTerminalOutput(rawOutput);
    if (tail) errors.push(`Last terminal output before exit:\n${tail}`);
    return errors;
  }
}
